import React, { Component } from "react";
import { NavLink as Link } from "react-router-dom";
import { AsyncPaginate } from "react-select-async-paginate";
import Select from "react-select";
import filterImg from "./../../assets/images/filter.svg";
import supervisorIcon from "./../../assets/images/supervisor_icon.svg";
import productManagerIcon from "./../../assets/images/product_manager_icon.svg";

import AddManagerSupervisor from "./AddManagerSupervisor";
import AddEmployee from "./AddEmployee";
import HistoryReport from "./HistoryReport";
import SendSms from "./SendSms";
import EmpList from "./EmpList";
import SmsHistory from "./SmsHistory";

import {
  callBackendAPI,
  setOrGetCookiesValue,
  generateSource,
  formatPhoneNumber,
} from "./../../helpers/common";
import { translate } from "./../../helpers/intl_helpers";
import {
  STATUS_SUCCESS,
  DEPARTMENT_DROPDOWN,
  PROJECT_MANAGER_TYPE,
  SUPERVISOR_TYPE,
} from "./../../config/global_constants";

import closeIcon from "./../../assets/images/close_icon.svg";
import searchIcon from "./../../assets/images/search_icon.svg";
import threeDotsIcon from "./../../assets/images/three_dot_icon.svg";
import editIcon from "./../../assets/images/edit-icon-white.svg";
import addIcon from "./../../assets/images/add_blue_icon.svg";

class OnCall extends Component {
  source;

  constructor(props) {
    super(props);
    const scheduleData = this.generateScheduleData();

    const today = new Date();
    const currentMonth = `${today.toLocaleString("en-US", { month: "long" })},${today.getFullYear()}`;
    const weeks = this.generateWeeks(today);

    this.state = {
      scheduleData,
      selectedMonth: currentMonth,
      weeks,
      activeTab: 0,
      showSearch: false,
      showProjectManagerModel: false,
      showAddEmployee: false,
      showHistoryReportModel: false,
      showSendSmsModel: false,
      showSmsHistoryModel: false,
      skillList: [],
      skillListFilter: [],
      skillsObj: {},
      managerDetails: {},
      supervisorDetails: {},
      skillId: "",
      locationList: [],
      customerList: [],
      searchStats: {},
      searchText: "",
    };
    this.resetSearchStats = this.resetSearchStats.bind(this);
    this.loadOptionsCustomer = this.loadOptionsCustomer.bind(this);
  }

  resetSearchStats = () => {
    this.setState({ searchStats: {}, currentPage: 1 }, () => {
      this.getOnCallList();
      this.getSkillList();
    });
  };

  componentDidMount() {
    this.setCurrentWeek();
    this.getMasterList();
    this.getSkillList();
  }

  // Generates month-based schedule (used only for the month dropdown options)
  generateScheduleData = (year = new Date().getFullYear()) => {
    const data = {};
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      const monthName = new Date(year, monthIndex).toLocaleString("en-US", {
        month: "long",
      });
      data[`${monthName},${year}`] = { monthIndex, year };
    }
    return data;
  };

  // Generates 7 rolling weeks starting one week before the Monday of the week containing referenceDate
  generateWeeks = (referenceDate = new Date()) => {
    const dayOfWeek = referenceDate.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const startMonday = new Date(referenceDate);
    startMonday.setDate(referenceDate.getDate() + daysToMonday);
    startMonday.setHours(0, 0, 0, 0);

    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);

    return Array.from({ length: 7 }, (_, idx) => {
      const i = idx - 1;
      const from = new Date(startMonday);
      from.setDate(startMonday.getDate() + i * 7);
      const to = new Date(from);
      to.setDate(from.getDate() + 6);

      const fromMonth = from.toLocaleString("en-US", { month: "short" });
      const toMonth = to.toLocaleString("en-US", { month: "short" });
      const tabLabel =
        fromMonth === toMonth
          ? `${fromMonth} ${from.getDate()}-${to.getDate()}`
          : `${fromMonth} ${from.getDate()} - ${toMonth} ${to.getDate()}`;

      /** The leftmost tab (i === -1) is always meant to read "Last Week" by position,
       * regardless of which month is being browsed. Checking it first prevents its date
       * range from being mislabeled when it coincidentally overlaps the real today's own
       * "next week"/"last week" window (e.g. browsing to a different month). */
      let label;
      if (i === -1) label = "Last Week";
      else if (today >= from && today <= to) label = "This Week";
      else if (nextWeek >= from && nextWeek <= to) label = "Next Week";
      else if (lastWeek >= from && lastWeek <= to) label = "Last Week";
      else label = `Week ${i + 1}`;

      return { label, from, to, tabLabel };
    });
  };

  setCurrentWeek = () => {
    const { weeks } = this.state;
    const today = new Date();
    const idx = weeks.findIndex((w) => today >= w.from && today <= w.to);
    /** getOnCallList reads activeTab from state, so it must wait for this setState to land -
     * calling it as a separate, immediately-following statement in componentDidMount read the
     * stale initial activeTab (0), fetching "Last Week" while the UI showed "This Week" active. */
    this.setState({ activeTab: idx >= 0 ? idx : 0 }, () => {
      this.getOnCallList();
    });
  };

  getOnCallList = () => {
    this.source = generateSource();
    window.showLoader();
    const { weeks, activeTab, searchStats, searchText } = this.state;
    const weekData = weeks[activeTab] || weeks[0];

    let apiReq = {
      model: "on_call",
      method: "list",
      start_date: weekData?.from,
      end_date: weekData?.to,
    };

    if (searchStats && Object.keys(searchStats).length) {
      if (searchStats?.skills && searchStats?.skills.length) {
        this.getSkillList(searchStats.skills.map((e) => e.value));
      }
      if (searchStats.location && searchStats.location.length)
        apiReq.location = searchStats.location.map((e) => e.value);
      if (searchStats.department && searchStats.department.length)
        apiReq.department = searchStats.department.map((e) => e.value);
      if (searchStats.skills && searchStats.skills.length)
        apiReq.skill_id = searchStats.skills.map((e) => e.value);
      if (searchStats.customer && searchStats.customer)
        apiReq.customer_code = searchStats.customer?.value;
      if (searchStats.customer && searchStats.customer)
        apiReq.customer_name = searchStats.customer?.customer_name;
      if (searchStats.job_title && searchStats.job_title.value)
        apiReq.job_title = searchStats.job_title.value;
      if (searchStats.full_name && searchStats.full_name.value)
        apiReq.full_name = searchStats.full_name.value;
    }
    if (searchText) apiReq.search = searchText || "";

    setOrGetCookiesValue("searchStatsEmployeeList", true, searchStats);
    callBackendAPI([apiReq], this.source)
      .then((response) => {
        window.hideLoader();
        if (response.success && response.data[0].status === STATUS_SUCCESS) {
          this.setState({
            skillsObj: response?.data?.[0]?.skills || [],
            managerDetails: response?.data?.[0]?.managerDetails || {},
            supervisorDetails: response?.data?.[0]?.supervisorDetails || {},
          });
        }
      })
      .catch((err) => console.log(err));
  };

  handleExport = () => {
    this.source = generateSource();
    window.showLoader();
    const { weeks, activeTab, searchStats, searchText } = this.state;
    const weekData = weeks[activeTab] || weeks[0];

    let apiReq = {
      model: "on_call",
      method: "export-schedules",
      start_date: weekData?.from,
      end_date: weekData?.to,
    };

    if (searchStats && Object.keys(searchStats).length) {
      if (searchStats?.skills && searchStats?.skills.length) {
        this.getSkillList(searchStats.skills.map((e) => e.value));
      }
      if (searchStats.location && searchStats.location.length)
        apiReq.location = searchStats.location.map((e) => e.value);
      if (searchStats.department && searchStats.department.length)
        apiReq.department = searchStats.department.map((e) => e.value);
      if (searchStats.skills && searchStats.skills.length)
        apiReq.skill_id = searchStats.skills.map((e) => e.value);
      if (searchStats.customer && searchStats.customer)
        apiReq.customer_code = searchStats.customer?.value;
      if (searchStats.customer && searchStats.customer)
        apiReq.customer_name = searchStats.customer?.customer_name;
      if (searchStats.job_title && searchStats.job_title.value)
        apiReq.job_title = searchStats.job_title.value;
      if (searchStats.full_name && searchStats.full_name.value)
        apiReq.full_name = searchStats.full_name.value;
    }
    if (searchText) apiReq.search = searchText || "";

    setOrGetCookiesValue("searchStatsEmployeeList", true, searchStats);
    callBackendAPI([apiReq], this.source)
      .then((response) => {
        window.hideLoader();
        if (response.data[0].file_url) {
          const link = document.createElement("a");
          link.href = response.data[0].file_url;
          link.rel = "noopener noreferrer"; // Security for '_blank' links
          document.body.appendChild(link); // Required for Firefox
          link.click();
          document.body.removeChild(link);
        }
      })
      .catch((err) => console.log(err));
  };

  getMasterList = () => {
    this.source = generateSource();
    window.showLoader();
    callBackendAPI(
      [
        {
          model: "home",
          method: "masterList",
          type: ["skill_set", "location"],
        },
      ],
      this.source,
    )
      .then((response) => {
        window.hideLoader();
        if (
          response.success &&
          response.data[0].status === STATUS_SUCCESS &&
          response.data[0].result
        ) {
          this.setState({
            isLoading: false,
            skillListFilter: response.data[0].result?.skill_set,
            locationList: response.data[0].result?.location,
          });
        }
      })
      .catch((err) => console.log(err));
  };

  getSkillList = (skillId = "") => {
    this.source = generateSource();
    window.showLoader();
    callBackendAPI(
      [
        {
          model: "home",
          method: "masterData",
          type: "skill_set",
          skill_set_id: skillId,
          show_on_schedule: true,
          is_all: true,
        },
      ],
      this.source,
    )
      .then((response) => {
        window.hideLoader();
        if (
          response.success &&
          response.data[0].status === STATUS_SUCCESS &&
          response.data[0].result
        ) {
          this.setState({
            isLoading: false,
            skillList: response.data[0].result || [],
          });
        }
      })
      .catch((err) => console.log(err));
  };

  async loadOptionsCustomer(inputValue, loadedOptions, { page }) {
    try {
      this.source = generateSource();
      let response = await callBackendAPI(
        [
          {
            model: "customer",
            method: "customer-code-list",
            current_page: page,
            search: inputValue ? inputValue : "",
          },
        ],
        this.source,
      );
      let resData = response?.data?.[0] || {};
      let result = resData.result || [];
      let recordsTotal = resData.recordsTotal || 0;
      let completeArray = result.concat(loadedOptions);
      return {
        options: result,
        hasMore: completeArray.length !== recordsTotal,
        additional: { page: page + 1 },
      };
    } catch (error) {
      console.error("Error loading options:", error);
      return { options: [], hasMore: false, additional: { page } };
    }
  }

  handleMonthChange = (e) => {
    const selectedMonth = e.target.value;
    const [monthName, year] = selectedMonth.split(",");
    const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
    const firstDay = new Date(parseInt(year), monthIndex, 1);
    const weeks = this.generateWeeks(firstDay);

    this.setState({ selectedMonth, weeks, activeTab: 1 }, () => {
      this.getOnCallList();
    });
  };

  handleNextMonth = () => {
    const { scheduleData, selectedMonth } = this.state;
    const months = Object.keys(scheduleData);
    const index = months.indexOf(selectedMonth);

    if (index < months.length - 1) {
      const nextMonth = months[index + 1];
      const [monthName, year] = nextMonth.split(",");
      const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
      const firstDay = new Date(parseInt(year), monthIndex, 1);
      const weeks = this.generateWeeks(firstDay);

      this.setState({ selectedMonth: nextMonth, weeks, activeTab: 1 }, () => {
        this.getOnCallList();
      });
    }
  };

  handleToggle = (e) => {
    e.preventDefault();
    this.setState(
      (prevState) => {
        const isClosing = prevState.showSearch;
        return {
          showSearch: !prevState.showSearch,
          searchText: isClosing ? "" : prevState.searchText,
        };
      },
      () => {
        if (!this.state.showSearch) this.getOnCallList("");
      },
    );
  };

  requestAddModal = (val, type, editId, weekData) => {
    this.setState({
      showProjectManagerModel: val,
      modalType: type || "",
      editId,
      selectedWeekData: weekData || null,
    });
  };

  requestOncallModal = (val, skillId = "", isNewSkillAdd = false) => {
    this.setState({ showAddEmployee: val, skillId: skillId || "" }, () => {
      if (!val) {
        this.getSkillList();
        this.getOnCallList();
      }
    });
  };

  historyReportModal = (val) => {
    this.setState({ showHistoryReportModel: val });
  };

  handelSendSms = (val) => {
    this.setState({ showSendSmsModel: val });
  };

  handelSmsHistory = (val) => {
    this.setState({ showSmsHistoryModel: val });
  };

  handleChangePage = (newPage = 1, e) => {
    if (e) e.preventDefault();
    this.setState({ currentPage: newPage }, () => {
      this.getOnCallList();
    });
  };

  handleChange = (fieldName) => (fieldVal) => {
    if (fieldName === "selectedFields") {
      let tmpArray = [];
      if (fieldVal) {
        fieldVal = fieldVal.constructor !== Array ? [fieldVal] : fieldVal;
        tmpArray = fieldVal.map((element) => element.value);
      }
      if (tmpArray.length >= 5) this.setState({ selectedFields: tmpArray });
    } else {
      let updatedFields = { ...this.state.searchStats, [fieldName]: fieldVal };
      if (fieldName === "location" && fieldVal.length === 0)
        updatedFields["department"] = null;
      if (fieldName === "job_number") updatedFields["work_order_number"] = null;
      this.setState({ searchStats: updatedFields });
    }
  };

  render() {
    const {
      selectedMonth,
      scheduleData,
      weeks,
      activeTab,
      skillList,
      skillsObj,
      managerDetails,
      supervisorDetails,
      locationList,
      searchStats,
      skillListFilter,
    } = this.state;
    const weekData = weeks[activeTab] || weeks[0];

    const formatDisplayDate = (date) =>
      date
        ? date.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })
        : "";

    return (
      <>
        <div className="dashboard-content min-height-72vh on-call-page">
          <div className="dashboard-heading d-flex justify-content-between align-items-center section-head filter-specfic">
            <div className="left-heading d-flex align-items-md-center align-items-start flex-md-row flex-column gap-2">
              <h1 className="flex-shrink-0">
                {translate("on_call.on_call_schedule")}
              </h1>

              <select
                className="form-select schedule-dates"
                aria-label="Default select example"
                value={selectedMonth}
                onChange={this.handleMonthChange}
              >
                {Object.keys(scheduleData).map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
            <div className="right-button">
              <div className=" roster-table-search table-search-web show">
                {/*<div className={`roster-table-search table-search-web ${this.state.showSearch ? "show" : ""}`}>*/}
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={translate("on_call.search_by_name_phone")}
                    value={this.state.searchText}
                    onChange={(e) =>
                      this.setState({ searchText: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        this.getOnCallList(this.state.searchText);
                      }
                    }}
                  />
                  <button className="btn-search" type="button">
                    {" "}
                    <img src={searchIcon} alt="img" />
                  </button>
                </div>
              </div>

              {/* <button className="btn btn-fill oncall-btn table-search-btn btn-white me-3" onClick={this.handleToggle}>
							<img src={this.state.showSearch ? closeIcon : searchIcon} alt="Img" width={29} height={26} />
						</button> */}
              <Link
                to="#"
                className="btn btn-fill oncall-btn table-search-btn btn-white me-3 add-skill-btn"
                onClick={() => this.requestOncallModal(true, "", true)}
              >
                <img src={addIcon} alt="img" />
              </Link>
              <div className="dropdown cstm-dropdown">
                <Link
                  to="#"
                  className="btn btn-fill oncall-btn oncall-action-btn btn-white me-3 dropdown-toggle"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={threeDotsIcon} alt="Img" width={29} height={26} />
                </Link>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item add-skill-btn"
                      onClick={() => this.requestOncallModal(true, "", true)}
                    >
                      Add
                    </Link>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => this.historyReportModal(true)}
                    >
                      {translate("on_call.history")}
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => this.handelSendSms(true)}
                    >
                      {translate("on_call.send_sms")}
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => this.handelSmsHistory(true)}
                    >
                      {translate("on_call.sms_history")}
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => this.handleExport()}
                    >
                      {translate("on_call.export")}
                    </button>
                  </li>
                </ul>
              </div>
              <Link
                to="#"
                className="btn btn-fill btn-filter collapsed me-0"
                data-bs-toggle="collapse"
                data-bs-target="#reportJobCollapseOne"
                aria-expanded="false"
                aria-controls="collapseOne"
              >
                <img src={filterImg} alt="Img" width={29} height={26} />
              </Link>
            </div>
          </div>

          <div className="filter-box">
            <div className="accordion" id="reportJobAccordionExample">
              <div className="accordion-item">
                <div
                  id="reportJobCollapseOne"
                  className="accordion-collapse collapse"
                  data-bs-parent="#reportJobAccordionExample"
                >
                  <div className="accordion-body">
                    <div className="filter-box-header d-flex justify-content-between mb-3">
                      <h3 className="mb-0">{translate("employee.filter")}</h3>
                      <Link
                        to="#"
                        className="btn-filter-close"
                        data-bs-toggle="collapse"
                        data-bs-target="#reportJobCollapseOne"
                        aria-expanded="false"
                        aria-controls="reportJobCollapseOne"
                      >
                        <img src={closeIcon} alt="Img" width={20} height={20} />
                      </Link>
                    </div>
                    <div className="adduser-form">
                      <form
                        className="adduser-form"
                        onSubmit={(e) => {
                          this.handleChangePage(1, e);
                        }}
                      >
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label className="mb-1 form-label">
                                {translate("on_call.location")}
                              </label>
                              <Select
                                className="custom-react-select"
                                inputId="search_by_location"
                                value={
                                  searchStats["location"]
                                    ? searchStats["location"]
                                    : null
                                }
                                options={locationList}
                                onChange={this.handleChange("location")}
                                closeMenuOnSelect={true}
                                isClearable={true}
                                isMulti
                                placeholder={translate("employee.location")}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label className="mb-1 form-label">
                                {translate("on_call.department")}
                              </label>
                              <Select
                                className="custom-react-select"
                                options={
                                  searchStats.location &&
                                  Array.isArray(searchStats.location)
                                    ? DEPARTMENT_DROPDOWN.filter((dept) =>
                                        searchStats.location.some(
                                          (loc) =>
                                            loc.department &&
                                            loc.department.includes(dept.value),
                                        ),
                                      )
                                    : []
                                }
                                closeMenuOnSelect={true}
                                isMulti
                                placeholder={translate(
                                  "calendar.all_department",
                                )}
                                value={
                                  searchStats.department
                                    ? searchStats.department
                                    : null
                                }
                                onChange={this.handleChange("department")}
                                isClearable={true}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label className="mb-1 form-label">
                                {translate("on_call.skill_set")}
                              </label>
                              <Select
                                className="custom-react-select"
                                inputId="search_by_skills"
                                value={
                                  searchStats["skills"]
                                    ? searchStats["skills"]
                                    : null
                                }
                                options={skillListFilter}
                                onChange={this.handleChange("skills")}
                                closeMenuOnSelect={true}
                                isClearable={true}
                                isMulti
                                placeholder={translate("on_call.select_skill")}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label className="mb-1 form-label">
                                {translate("on_call.customer")}
                              </label>
                              <AsyncPaginate
                                className="custom-react-select"
                                inputId="search_by_customer"
                                key={
                                  searchStats?.customer
                                    ? searchStats?.customer
                                    : null
                                }
                                value={
                                  searchStats?.customer
                                    ? searchStats?.customer
                                    : null
                                }
                                loadOptions={this.loadOptionsCustomer}
                                onChange={this.handleChange("customer")}
                                additional={{ page: 1, type: "customer" }}
                                isClearable={true}
                                placeholder={translate(
                                  "on_call.select_customer",
                                )}
                              />
                            </div>
                          </div>
                          <div className="col-md-12 d-flex align-items-center">
                            <button
                              className="btn btn-fill me-3"
                              type="submit"
                              onClick={(e) => {
                                this.handleChangePage(1, e);
                              }}
                            >
                              {translate("system.submit")}
                            </button>
                            <button
                              className="btn btn-outline"
                              onClick={() => this.resetSearchStats()}
                            >
                              {translate("system.reset")}
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="roster-table-search table-search-mobile show">
            <div className="form-group">
              <input
                type="text"
                className="form-control"
                placeholder={translate("on_call.search_by_name_phone")}
                value={this.state.searchText}
                onChange={(e) => this.setState({ searchText: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    this.getOnCallList(this.state.searchText);
                  }
                }}
              />
              <button className="btn-search" type="button">
                {" "}
                <img src={searchIcon} alt="img" />
              </button>
            </div>
            {/* <div className="form-group">
						<input type="text" className="form-control" placeholder={translate('on_call.search_b_name_phone')} />
						<button className="btn-search" type="button"> <img src={searchIcon} alt="img" /></button>
					</div> */}
          </div>

          <div className="segmented-control">
            {weeks.map((week, i) => (
              <button
                key={i}
                type="button"
                className={`segment ${activeTab === i ? "active" : ""}`}
                onClick={() => {
                  this.setState({ activeTab: i }, () => {
                    this.getOnCallList();
                  });
                }}
              >
                <span className="week-name">{week.label}</span>
                <span className="week-date">{week.tabLabel}</span>
              </button>
            ))}
            <button
              type="button"
              className="segment"
              onClick={this.handleNextMonth}
            >
              {translate("on_call.next_month")}
            </button>
          </div>

          <section className="info-cards">
            <div className="status-card oncall-status-card">
              <div className="card-icon neutral-icon align-self-start">
                <img src={productManagerIcon} alt="img" />
              </div>
              <div className="card-details w-100">
                <h3 className="card-label mb-0">
                  {translate("on_call.project_manager")}
                </h3>
                <ul className="oncall-week-list">
                  <li className="oncall-week-item">
                    <strong className="oncall-week-name">
                      {managerDetails?.name || translate("on_call.not_set")}
                    </strong>
                    <div className="oncall-week-actions">
                      <span className="oncall-week-date">
                        {formatDisplayDate(weekData?.from)} –{" "}
                        {formatDisplayDate(weekData?.to)}
                      </span>
                      <Link
                        to="#"
                        className="oncall-edit-btn"
                        onClick={() =>
                          this.requestAddModal(
                            true,
                            PROJECT_MANAGER_TYPE,
                            null,
                            weekData,
                          )
                        }
                      >
                        <img src={editIcon} alt="Edit" />
                      </Link>
                    </div>
                  </li>
                </ul>
                <div className="mob-number">
                  <span>{translate("on_call.phone")} : </span>
                  <a href={`tel:${managerDetails?.mobile}`}>
                    {formatPhoneNumber(managerDetails?.mobile) || "N/A"}
                  </a>
                </div>
              </div>
            </div>
            <div className="status-card oncall-status-card">
              <div className="card-icon align-self-start">
                <img src={supervisorIcon} alt="img" />
              </div>
              <div className="card-details w-100">
                <h3 className="card-label mb-0">
                  {translate("on_call.week_supervisor")}
                </h3>
                <ul className="oncall-week-list">
                  <li className="oncall-week-item">
                    <strong className="oncall-week-name">
                      {supervisorDetails?.name || translate("on_call.not_set")}
                    </strong>

                    <div className="oncall-week-actions">
                      <span className="oncall-week-date">
                        {formatDisplayDate(weekData?.from)} –{" "}
                        {formatDisplayDate(weekData?.to)}
                      </span>
                      <Link
                        to="#"
                        className="oncall-edit-btn"
                        onClick={() =>
                          this.requestAddModal(
                            true,
                            SUPERVISOR_TYPE,
                            null,
                            weekData,
                          )
                        }
                      >
                        <img src={editIcon} alt="Edit" />
                      </Link>
                    </div>
                  </li>
                </ul>
                <div className="mob-number">
                  <span>{translate("on_call.phone")} : </span>
                  <a href={`tel:${supervisorDetails?.mobile}`}>
                    {formatPhoneNumber(supervisorDetails?.mobile) || "N/A"}
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/**Employee listing data skill wise */}
          <EmpList
            skillList={skillList}
            skillsObj={skillsObj}
            requestOncallModal={this.requestOncallModal}
          />
        </div>

        {/* Add project Manager/Supervisor modal */}
        {this.state.showProjectManagerModel ? (
          <AddManagerSupervisor
            requestAddModal={this.requestAddModal}
            modalType={this.state.modalType}
            weekData={this.state.selectedWeekData}
            getOnCallList={this.getOnCallList}
          />
        ) : null}

        {/* Oncall schedule modal */}
        {this.state.showAddEmployee ? (
          <AddEmployee
            requestOncallModal={this.requestOncallModal}
            weekData={weekData}
            skillId={this.state.skillId}
            getOnCallList={this.getOnCallList}
          />
        ) : null}

        {/* History report modal */}
        {this.state.showHistoryReportModel ? (
          <HistoryReport
            historyReportModal={this.historyReportModal}
            weekData={weekData}
          />
        ) : null}

        {/* Send SMS modal */}
        {this.state.showSendSmsModel ? (
          <SendSms
            handelSendSms={this.handelSendSms}
            getOnCallList={this.getOnCallList}
          />
        ) : null}

        {/* Send SMS history modal */}
        {this.state.showSmsHistoryModel ? (
          <SmsHistory handelSmsHistory={this.handelSmsHistory} />
        ) : null}
      </>
    );
  }
}
export default OnCall;
